export interface PortalRepairQuoteLine {
  quantity: number | string
  unitPrice: number | string
  subtotal?: number | string | null
}

export interface PortalRepair {
  id: string
  workOrderId?: string
  title: string
  description?: string | null
  status: string
  customerDecisionNote?: string | null
  submittedForApprovalAt?: string | null
  decidedAt?: string | null
  version?: number
  total?: number | string | null
  quoteLines: PortalRepairQuoteLine[]
  stageExecutionId?: string | null
  stageCode?: string | null
  stageTitle?: string | null
}

export interface PortalStage {
  id: string
  code: string
  title: string
}

function numberOrNull(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

export function repairLineSubtotal(line: PortalRepairQuoteLine): number {
  const providedSubtotal = numberOrNull(line.subtotal)
  if (providedSubtotal !== null) return providedSubtotal
  return (numberOrNull(line.quantity) ?? 0) * (numberOrNull(line.unitPrice) ?? 0)
}

export function repairTotal(repair: PortalRepair): number {
  return numberOrNull(repair.total) ?? repair.quoteLines.reduce((sum, line) => sum + repairLineSubtotal(line), 0)
}

export function visibleRepairs<T extends PortalRepair>(repairs: readonly T[]): T[] {
  return repairs.filter((repair) => repair.status !== 'draft')
}

export function stageTitleForRepair(repair: PortalRepair, stages: readonly PortalStage[] = []): string {
  if (repair.stageTitle?.trim()) return repair.stageTitle.trim()
  const stage = stages.find((candidate) => candidate.id === repair.stageExecutionId || candidate.code === repair.stageCode)
  return stage?.title ?? repair.stageCode ?? 'Etapa da restauração'
}

export interface RepairStageGroup<T extends PortalRepair = PortalRepair> {
  key: string
  title: string
  repairs: T[]
}

export function groupRepairsByStage<T extends PortalRepair>(repairs: readonly T[], stages: readonly PortalStage[] = []): RepairStageGroup<T>[] {
  const groups: RepairStageGroup<T>[] = []
  const byKey = new Map<string, RepairStageGroup<T>>()

  for (const repair of repairs) {
    const key = repair.stageCode ?? repair.stageExecutionId ?? stageTitleForRepair(repair, stages)
    let group = byKey.get(key)
    if (!group) {
      group = { key, title: stageTitleForRepair(repair, stages), repairs: [] }
      byKey.set(key, group)
      groups.push(group)
    }
    group.repairs.push(repair)
  }

  return groups
}
